// see: https://github.com/TanStack/query/discussions/5279
import type { UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

type QueryEvents<RespT, ErrT> = {
    onSuccess: (resp: RespT) => void;
    onError: (resp: ErrT) => void;
};

function useQueryEvents<RespT, ErrT>(query: UseQueryResult<RespT, ErrT>, callbacks: Partial<QueryEvents<RespT, ErrT>>) {
    const { onSuccess, onError } = callbacks;

    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    // keep callback refs up-to-date with latest callback functions
    useEffect(() => {
        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
    }, [onSuccess, onError]);

    // trigger onSuccess callback when query data is available
    useEffect(() => {
        if (query.data && onSuccessRef.current) {
            onSuccessRef.current(query.data);
        }
    }, [query.data]);

    // trigger onError callback when query error occurs
    useEffect(() => {
        if (query.error && onErrorRef.current) {
            onErrorRef.current(query.error);
        }
    }, [query.error]);

    return {
        data: query.data,
        error: query.error,
        isFetching: query.isFetching,
        refetch: query.refetch,
    };
}

export { useQueryEvents };
